import {CartFactory} from "../cart";
import {getShopId} from "./../../components/lib/getShopId";
import {CustomerFactory} from "./../customer/CustomerFactory";
import {CheckoutFactory} from "./../checkout/CheckoutFactory";
import {ICustomerAddress} from "typescript-library/customer/requestHandlers/CustomerRequestHandler";

enum FormSource {
  CustomerDeliveryAddressForm = "CustomerDeliveryAddressForm",
  CustomerInvoiceAddressForm = "CustomerInvoiceAddressForm",
}

/**
 * Additional handler to submit address forms to shop API (customer and checkout) before the default form submission (ecs)
 * @param {SubmitEvent} event
 */
export async function submitFormToShopApi(event: SubmitEvent) {
  event.preventDefault();

  const form = event.target as HTMLFormElement;
  const visitorId = window.c_UserUuidOrig;
  const shopId = getShopId();
  const formId = form.id;
  const formData = new FormData(form);
  const isInvoiceAddress = formId === FormSource.CustomerInvoiceAddressForm;
  const isDeliveryAddress =
    formId === FormSource.CustomerDeliveryAddressForm || formData.get("IsAlsoDeliveryAddress") === "True";

  if (!isValidFormId(formId) || !shopId || !visitorId) return;

  try {
    await Promise.all([
      updateCustomerAddresses(shopId, formData, isInvoiceAddress, isDeliveryAddress),
      updateCheckoutAddresses(shopId, visitorId, formData, isInvoiceAddress, isDeliveryAddress),
    ]);
  } catch (error) {
  } finally {
    form.submit();
  }
}

async function updateCustomerAddresses(
  shopId: number,
  formData: FormData,
  isLastInvoiceAddress: boolean,
  isLastDeliveryAddress: boolean,
) {
  const customer = await CustomerFactory.getCustomer();

  // Remove empty fields. They lead to errors
  const newAddress = Object.fromEntries(
    Object.entries(mapFormToAddressData(formData)).filter(([_, value]) => !!value),
  ) as any as Omit<ICustomerAddress, "isLastInvoiceAddress" | "isLastDeliveryAddress" | "addressId">;

  const newCustomerAddress = {
    ...newAddress,
    isLastInvoiceAddress,
    isLastDeliveryAddress,
  };
  return await customer.postAddress(shopId, newCustomerAddress);
}

async function updateCheckoutAddresses(
  shopId: number,
  visitorId: string,
  formData: FormData,
  isInvoiceAddress: boolean,
  isDeliveryAddress: boolean,
) {
  // We need the cart to get the checkout to then be able to put the new address
  const cart = await CartFactory.getCartForVisitorId(shopId, visitorId);
  if (!cart) return;

  const checkout = await CheckoutFactory.generateCheckout(shopId, cart);
  const hasAddress = !!checkout.deliveryAddress || !!checkout.invoiceAddress;

  if (!hasAddress) return;

  const newAddress = {...mapFormToAddressData(formData), dateOfBirth: checkout.invoiceAddress?.dateOfBirth ?? null}; // DOB is not part of the form

  return await checkout.put({
    address: {
      invoice: isInvoiceAddress ? newAddress : undefined,
      delivery: isDeliveryAddress ? newAddress : undefined,
    },
  });
}

function isValidFormId(formId): formId is FormSource {
  return Object.values(FormSource).includes(formId as FormSource);
}

function mapFormToAddressData(formData: FormData) {
  return {
    gender: (formData.get("Gender") as "M" | "F" | "") || null,
    title: (formData.get("Title") as string | null) || "",
    firstName: (formData.get("Firstname") as string | null) || "",
    lastName: (formData.get("Lastname") as string | null) || "",
    street: (formData.get("Street") as string | null) || "",
    number: (formData.get("Number") as string | null) || "",
    zip: (formData.get("Zip") as string | null) || "",
    city: (formData.get("City") as string | null) || "",
    countryCode: (formData.get("CountryId") as string | null) || "",
    state: (formData.get("State") as string | null) || "",
    email: (formData.get("Email") as string | null) || "",
    phone: (formData.get("Phone") as string | null) || "",
    company: (formData.get("Company") as string | null) || "",
    department: (formData.get("Department") as string | null) || "",
  };
}
